import { Dispatch } from '@reduxjs/toolkit';
import { Line } from 'components/common/types/Deal.types';
import {
  DirectSales,
  DirectSalesAllocationSolutionResponse,
  DirectSalesCampaignDealLineResponse,
} from 'components/common/types/DirectSalesCampaign.types';
import { ToastProps } from 'lib/Toaster';
import { isEmpty } from 'lodash';
import { updateDirectSalesDealLine } from 'modules/api/directSalesCampaign';
import {
  addDirectSalesLineId,
  changeActiveDirectSalesLine,
  changeDealCurrentLineData,
  changeDealData,
  changeDirectSalesData,
  clearDirectSalesAssetList,
  clearDirectSalesPrices,
  hidePlannerSecondaryPanel,
  setDirectSalesStates,
  setIsFetchingAdvancedAvailabilityReport,
  startLoading,
  stopLoading,
  updateBackupFormDataLine,
  updateCurrentLineAndBackupLineProperty,
} from 'store/dealManagement/reducer';
import { clearActiveObjective } from 'store/objective/reducer';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import { cancelFunctions } from 'utils/cancelFunctions';
import { LocalLoading } from 'store/dealManagement/types';
import { addToastNotification } from 'store/notification/reducer';
import { checkDirectSalesAvailabilityAction } from 'store/dealManagement/actions/checkDirectSalesAvailability';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { getIsAnyLinePriced } from 'utils/getIsAnyLinePriced';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getMarketId } from 'store/publisher/selectors';
import buildLinesStatesWithAllocation from '../utils/buildLinesStatesWithAllocation';
import useRetrieveDirectSalesCampaign from './useRetrieveDirectSalesCampaign';
import {
  transformAllLineData,
  transformDirectSalesDealLineRequest,
} from '../utils/transformDirectSalesCampaignRequests';
import {
  DispatchCurrentLineUpdatesParams,
  HandleLineChangesParams,
  UseEditDirectSalesLine,
} from './useEditDirectSalesLine.types';
import { useDirectSalesPricedSolution } from '../useDirectSalesPricedSolution';

const dispatchCurrentLineUpdates = ({
  dispatch,
  directSalesData,
  currentLineData,
}: DispatchCurrentLineUpdatesParams): void => {
  if (!isEmpty(directSalesData)) dispatch(changeDirectSalesData(directSalesData));
  if (!isEmpty(currentLineData)) dispatch(changeDealCurrentLineData(currentLineData));
  dispatch(clearDirectSalesPrices());
  dispatch(clearDirectSalesAssetList());
  dispatch(clearActiveObjective());
  dispatch(hidePlannerSecondaryPanel());
};

const dispatchLoadingStart = (dispatch: Dispatch, currentLineId: string, localLoading?: LocalLoading): void => {
  if (localLoading) {
    dispatch(startLoading(localLoading));
    return;
  }
  dispatch(updateCurrentLineAndBackupLineProperty({ lineId: currentLineId, type: 'isSaving', value: true }));
};

const dispatchLoadingStop = (dispatch: Dispatch, currentLineId: string, localLoading?: LocalLoading): void => {
  if (localLoading) {
    dispatch(stopLoading(localLoading));
    return;
  }
  dispatch(updateCurrentLineAndBackupLineProperty({ lineId: currentLineId, type: 'isSaving', value: false }));
};

const handleLineChanges = ({
  dispatch,
  currentLineId,
  directSalesData,
  currentLineData,
  successToast,
  isEditingDifferentLineFromOverviewMode,
}: HandleLineChangesParams): void => {
  const hasChanges = !isEmpty(directSalesData) || !isEmpty(currentLineData);
  if (!hasChanges) return;

  if (isEditingDifferentLineFromOverviewMode) dispatch(changeActiveDirectSalesLine(currentLineId));
  dispatchCurrentLineUpdates({ dispatch, directSalesData, currentLineData });

  dispatch(
    updateBackupFormDataLine({
      lineId: currentLineId,
      newBackupLineData: { ...currentLineData, ...directSalesData },
    }),
  );

  if (successToast) dispatch(addToastNotification(successToast));
};

const handleSavedLine = (
  dispatch: Dispatch,
  savedLineId: string | undefined,
  currentLineId: string,
  savedDealLines: DirectSalesCampaignDealLineResponse[],
  allocationSolution: DirectSalesAllocationSolutionResponse | null,
  campaignId?: string,
): void => {
  if (!savedLineId) return;

  dispatch(setDirectSalesStates(buildLinesStatesWithAllocation(savedDealLines, allocationSolution, campaignId)));

  if (!currentLineId) {
    dispatch(addDirectSalesLineId(savedLineId));
  }
};

const getSavedLine = ({
  currentLineId,
  updatedDealLines = [],
  savedDealLines = [],
}: {
  currentLineId: string;
  updatedDealLines: { id: string; name: string }[];
  savedDealLines: DirectSalesCampaignDealLineResponse[];
}): DirectSalesCampaignDealLineResponse | undefined => {
  const savedLineId = currentLineId || updatedDealLines?.[0]?.id;
  return savedDealLines.find((line) => line.id === savedLineId);
};

const useEditDirectSalesLine = (): UseEditDirectSalesLine => {
  const dispatch = useDispatch();
  const getUpdatedDirectSalesCampaign = useRetrieveDirectSalesCampaign();
  const handlePricedSolution = useDirectSalesPricedSolution();
  const plannerSecondaryPanelType = useSelector((state: Store) => state.dealManagement.plannerSecondaryPanelType);

  const directSales = useSelector((state: Store) => state.dealManagement.directSales);
  const campaignId = useSelector(getDealId);
  const currentLine = useSelector(getCurrentLine);
  const backupFormDataLines = useSelector(getBackupLines, shallowEqual);
  const marketId = useSelector(getMarketId);

  return async ({
    lineId,
    currentLineData,
    directSalesData,
    localLoading,
    successToast,
  }: {
    lineId?: string;
    currentLineData?: Partial<Line>;
    directSalesData?: Partial<DirectSales>;
    localLoading?: LocalLoading;
    successToast?: ToastProps;
  } = {}): Promise<void> => {
    const currentLineId = lineId || currentLine.lineId;
    const isEditingDifferentLineFromOverviewMode = Boolean(lineId && lineId !== currentLine.lineId);

    const targetLineFromBackup = backupFormDataLines.find((line) => line.lineId === lineId);
    const baseLineData = isEditingDifferentLineFromOverviewMode ? targetLineFromBackup || currentLine : currentLine;
    const baseDirectSalesData = isEditingDifferentLineFromOverviewMode ? baseLineData : directSales;

    const dealLine = {
      ...baseLineData,
      ...baseDirectSalesData,
      ...currentLineData,
      ...directSalesData,
    };

    const [payload] = transformAllLineData({ lines: [dealLine] });
    const formData = transformDirectSalesDealLineRequest(payload);

    if (plannerSecondaryPanelType) dispatch(hidePlannerSecondaryPanel());
    dispatchLoadingStart(dispatch, currentLineId, localLoading);
    cancelFunctions.FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY_REPORT_EXPORT_ID?.();
    cancelFunctions.FETCH_ADVANCED_DIRECT_SALES_AVAILABILITY?.();
    dispatch(setIsFetchingAdvancedAvailabilityReport(false));

    try {
      const { id, dealLines: updatedDealLines } = await updateDirectSalesDealLine(
        formData,
        marketId,
        campaignId,
        currentLineId,
        cancelFunctions,
      );

      dispatchLoadingStop(dispatch, currentLineId, localLoading);

      if (campaignId !== id) dispatch(changeDealData({ dealId: id }));

      if (currentLineId) {
        handleLineChanges({
          dispatch,
          currentLineId,
          directSalesData,
          currentLineData,
          successToast,
          isEditingDifferentLineFromOverviewMode,
        });
      }

      const {
        campaign: {
          header,
          body: {
            campaignDefinition: { dealLines: savedDealLines },
            allocationSolution,
            pricingSolution,
          },
        },
      } = await getUpdatedDirectSalesCampaign();

      const savedLine = getSavedLine({ currentLineId, updatedDealLines, savedDealLines });

      if (pricingSolution && getIsAnyLinePriced(savedDealLines)) {
        await handlePricedSolution({
          campaignId,
          marketId,
          pricingSolution,
          lines: savedDealLines,
        });
      }

      dispatch(
        changeDealData({
          salesPerson: header.salesPerson || null,
          salesTeam: header.salesTeam || null,
          adminPerson: header.adminPerson || null,
        }),
      );

      handleSavedLine(dispatch, savedLine?.id, currentLineId, savedDealLines, allocationSolution, id);

      await dispatch(checkDirectSalesAvailabilityAction({ overrideLineId: currentLineId, cancelFunctions }));
    } catch {
      dispatchLoadingStop(dispatch, currentLineId, localLoading);
    }
  };
};

export default useEditDirectSalesLine;
